import { RecentlyViewed } from '@theme/recently-viewed-products';

class HDRecentlyViewedHome {
  constructor() {
    this.rails = [...document.querySelectorAll('[data-hd-recently-viewed-rail]')];
    if (this.rails.length === 0) return;

    this.rails.forEach((rail) => this.render(rail));
    document.addEventListener('shopify:section:load', (event) => {
      event.target
        ?.querySelectorAll?.('[data-hd-recently-viewed-rail]')
        ?.forEach((rail) => this.render(rail));
    });
  }

  render(rail) {
    const track = rail.querySelector('[data-hd-recent-track]');
    if (!track) return;

    const maxItems = Math.max(1, Math.min(Number(rail.dataset.maxItems || 8), 8));
    const fallbackSlots = [...track.querySelectorAll('[data-hd-recent-fallback-slot]')];
    const fallbackProducts = this.stableShuffle(fallbackSlots).map((slot) => ({
      id: String(slot.dataset.productId || ''),
      node: slot,
    }));
    const seenIds = new Set();
    const items = [];

    for (const product of RecentlyViewed.getProductRecords()) {
      if (items.length >= maxItems) break;
      if (!product.id || seenIds.has(product.id)) continue;

      seenIds.add(product.id);
      items.push(this.createRecentlyViewedSlot(product));
    }

    for (const product of fallbackProducts) {
      if (items.length >= maxItems) break;
      if (product.id && seenIds.has(product.id)) continue;

      if (product.id) seenIds.add(product.id);
      const slot = product.node.cloneNode(true);
      slot.hidden = false;
      slot.dataset.hdRecentSlotState = 'fallback';
      this.setBadge(slot, 'Must have');
      items.push(slot);
    }

    track.replaceChildren(...items);
    const emptyState = rail.querySelector('[data-hd-recent-empty]');
    if (emptyState) emptyState.hidden = items.length > 0;
  }

  createRecentlyViewedSlot(product) {
    const slot = document.createElement('div');
    slot.className = 'hd-recent-rail__slot';
    slot.dataset.hdRecentSlotState = 'recent';
    slot.dataset.productId = product.id;
    slot.innerHTML = `
      <span class="hd-recent-rail__badge">Recently viewed</span>
      <article class="hd-product-card hd-product-card--recently-viewed">
        <a class="hd-product-card__media" href="${this.escapeAttr(product.url)}">
          ${
            product.image
              ? `<img src="${this.escapeAttr(product.image)}" alt="${this.escapeAttr(product.title)}" loading="lazy">`
              : `<div class="hd-product-card__fallback">Recently viewed</div>`
          }
        </a>
        <div class="hd-product-card__info">
          <a href="${this.escapeAttr(product.url)}"><h3>${this.escapeHtml(product.title)}</h3></a>
          ${product.price ? `<span>${this.escapeHtml(product.price)}</span>` : ''}
        </div>
        <div class="hd-product-card__quick-add">
          <button
            class="hd-product-card__quick-add-button button"
            type="button"
            data-hd-quick-add-trigger
            data-product-url="${this.escapeAttr(product.url)}"
            data-product-handle="${this.escapeAttr(product.handle)}"
            data-product-title="${this.escapeAttr(product.title)}"
            data-product-image="${this.escapeAttr(product.image)}"
            data-product-price="${this.escapeAttr(product.price)}"
            data-product-id="${this.escapeAttr(product.id)}"
            aria-label="Choose options for ${this.escapeAttr(product.title)}"
          >
            <span class="svg-wrapper hd-product-card__quick-add-icon" aria-hidden="true">
              <svg viewBox="0 0 20 20" focusable="false"><path d="M9 3h2v6h6v2h-6v6H9v-6H3V9h6V3Z"/></svg>
            </span>
          </button>
        </div>
      </article>
    `;

    return slot;
  }

  setBadge(slot, label) {
    const existingBadge = slot.querySelector('.hd-recent-rail__badge');
    if (existingBadge) {
      existingBadge.textContent = label;
      return;
    }

    const badge = document.createElement('span');
    badge.className = 'hd-recent-rail__badge';
    badge.textContent = label;
    slot.prepend(badge);
  }

  stableShuffle(slots) {
    const seed = new Date().toISOString().slice(0, 10);
    return [...slots].sort((a, b) => {
      const aScore = this.hash(`${seed}:${a.dataset.productId || a.textContent || ''}`);
      const bScore = this.hash(`${seed}:${b.dataset.productId || b.textContent || ''}`);
      return aScore - bScore;
    });
  }

  hash(value) {
    let hash = 0;
    for (let index = 0; index < value.length; index += 1) {
      hash = (hash << 5) - hash + value.charCodeAt(index);
      hash |= 0;
    }
    return Math.abs(hash);
  }

  escapeHtml(value = '') {
    return String(value).replace(/[&<>]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[char]);
  }

  escapeAttr(value = '') {
    return this.escapeHtml(value).replace(/"/g, '&quot;');
  }
}

new HDRecentlyViewedHome();
